import { useEffect } from 'react';
import { companyProfile } from '../content/siteContent';

function setMeta(attribute, key, content) {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', url);
}

function setStructuredData(id, data) {
  let script = document.head.querySelector(`script[data-seo-schema="${id}"]`);

  if (!data) {
    if (script) {
      script.remove();
    }
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-schema', id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function buildAbsoluteUrl(path) {
  if (!path) {
    return window.location.href;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return new URL(path, window.location.origin).toString();
}

function Seo({ title, description, keywords = [], image = '/logo.jpg', type = 'website', schema, noIndex = false }) {
  useEffect(() => {
    const url = window.location.href;
    const keywordText = Array.isArray(keywords) ? keywords.join(', ') : keywords;
    const imageUrl = buildAbsoluteUrl(image);

    // Organization — used on all pages for brand signals
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      name: companyProfile.legalName,
      alternateName: companyProfile.brandName,
      url: companyProfile.websiteUrl,
      logo: buildAbsoluteUrl('/logo.jpg'),
      email: companyProfile.primaryEmail,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'E-106 Akshardham Heights, S.P. Ring Road, Vastral Road',
        addressLocality: 'Ahmedabad',
        addressRegion: 'Gujarat',
        postalCode: '382418',
        addressCountry: 'IN'
      },
      sameAs: [
        'https://vedanskrix.com'
      ]
    };

    // LocalBusiness schema — improves Google Maps & local search
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Vedanskrix IT Training & Job Placement',
      description: 'Professional IT training institute in Ahmedabad offering programming, web design, digital marketing, and placement assistance.',
      url: companyProfile.websiteUrl,
      image: buildAbsoluteUrl('/logo.jpg'),
      email: companyProfile.primaryEmail,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'E-106 Akshardham Heights, S.P. Ring Road, Vastral Road',
        addressLocality: 'Ahmedabad',
        addressRegion: 'Gujarat',
        postalCode: '382418',
        addressCountry: 'IN'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '23.0009',
        longitude: '72.6481'
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '09:00', closes: '19:00' }
      ],
      priceRange: '₹₹'
    };

    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url,
      inLanguage: 'en-IN',
      isPartOf: { '@type': 'WebSite', name: companyProfile.brandName, url: companyProfile.websiteUrl }
    };

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywordText);
    setMeta('name', 'robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    setMeta('name', 'author', companyProfile.legalName);
    setMeta('name', 'geo.region', 'IN-GJ');
    setMeta('name', 'geo.placename', 'Ahmedabad');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', companyProfile.brandName);
    setMeta('property', 'og:locale', 'en_IN');
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', imageUrl);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);
    setCanonical(url);
    setStructuredData('organization', organizationSchema);
    setStructuredData('localbusiness', localBusinessSchema);
    setStructuredData('webpage', webpageSchema);
    setStructuredData('page-specific', schema || null);
  }, [description, image, keywords, noIndex, schema, title, type]);

  return null;
}

export default Seo;
